import React from "react";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Paper,
  InputBase,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
} from "@mui/icons-material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

export default function CommentsComponent({
  topLevelComments,
  setEditCommentId,
  setNewComment,
  handleDeleteComment,
  setReplyTo,
  repliesByParent,
  replyTo,
  imagePreview,
  inputRef,
  editCommentId,
  newComment,
  loading,
  fileInputRef,
  handleAddOrEditComment,
  handleFileChange,
}) {
  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h5" gutterBottom>
        Comments
      </Typography>
      <List>
        {topLevelComments.length ? (
          topLevelComments.map((comment) => (
            <React.Fragment key={comment.id}>
              <ListItem alignItems="flex-start">
                <ListItemAvatar>
                  <Avatar>{comment?.userName?.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box>
                      <Typography fontWeight="bold">
                        {comment.userName || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(comment.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography>{comment.commentText}</Typography>
                      {comment.attachment && (
                        <Box mt={1}>
                          <img
                            src={comment.attachment}
                            alt="attachment"
                            style={{
                              maxWidth: "100%",
                              borderRadius: "5px",
                            }}
                          />
                        </Box>
                      )}
                    </>
                  }
                />
                <IconButton
                  onClick={() => {
                    setEditCommentId(comment.id);
                    setNewComment(comment.commentText);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteComment(comment.id)}>
                  <DeleteIcon />
                </IconButton>
                <IconButton onClick={() => setReplyTo(comment.id)}>
                  <ReplyIcon />
                </IconButton>
              </ListItem>

              {/* Render replies */}
              {repliesByParent[comment.id]?.map((reply) => (
                <ListItem key={reply.id} sx={{ pl: 6 }}>
                  <ListItemAvatar>
                    <Avatar>{reply.userName?.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold">
                        {reply.userName || "Unknown"} (reply)
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography>{reply.commentText}</Typography>
                        {reply.attachment && (
                          <Box mt={1}>
                            <img
                              src={reply.attachment}
                              alt="attachment"
                              style={{
                                maxWidth: "100%",
                                borderRadius: "5px",
                              }}
                            />
                          </Box>
                        )}
                      </>
                    }
                  />
                  <IconButton onClick={() => handleDeleteComment(reply.id)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}

              <Divider />
            </React.Fragment>
          ))
        ) : (
          <Typography>No comments yet. Add one!</Typography>
        )}
      </List>

      <Box mt={2}>
        {replyTo && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Replying to comment ID: {replyTo}{" "}
            <span
              style={{ color: "red", cursor: "pointer", marginLeft: 8 }}
              onClick={() => setReplyTo(null)}
            >
              ✕ Cancel
            </span>
          </Typography>
        )}
        {imagePreview && (
          <Box mt={2} display="flex" justifyContent="space-between">
            <Typography>Image Preview:</Typography>
            <img src={imagePreview} alt="Preview" style={{ maxWidth: "10%" }} />
          </Box>
        )}
        <Paper
          component="form"
          sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
        >
          <InputBase
            ref={inputRef}
            sx={{ ml: 1, flex: 1 }}
            placeholder={editCommentId ? "Edit comment" : "Add a comment"}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            inputProps={{ "aria-label": "add comment" }}
            disabled={loading}
          />
          <IconButton
            onClick={() => fileInputRef.current.click()}
            disabled={loading}
          >
            <AddPhotoAlternateIcon />
          </IconButton>
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton
            color="primary"
            sx={{ p: "10px" }}
            onClick={handleAddOrEditComment}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Paper>
        <input
          type="file"
          accept="image/*,application/pdf"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>
    </Paper>
  );
}
